import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { HERO_IMAGES } from "@/data/heroImages";
import { Shield, Lock, Database, UserCheck, Cookie, AlertTriangle } from "lucide-react";

const Section = ({ number, title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5 }}
    className="mb-12"
  >
    <div className="flex items-baseline gap-4 mb-5">
      <span className="font-display text-2xl md:text-3xl text-red-500 leading-none">{String(number).padStart(2, "0")}</span>
      <h2 className="font-display text-xl sm:text-2xl md:text-3xl text-navy-700 leading-tight">{title}</h2>
    </div>
    <div className="pl-0 md:pl-12 text-[15px] leading-[1.8] text-[#444] space-y-3">{children}</div>
  </motion.section>
);

const Bullet = ({ children }) => (
  <li className="flex gap-3">
    <span className="mt-2.5 inline-block h-1 w-1 bg-red-500 shrink-0" />
    <span className="text-[#444]">{children}</span>
  </li>
);

const RIGHT_ICONS = [
  { icon: UserCheck, label: "Droit d'accès" },
  { icon: Shield, label: "Droit de rectification" },
  { icon: Lock, label: "Droit de suppression" },
  { icon: AlertTriangle, label: "Droit d'opposition" },
  { icon: Database, label: "Droit à la limitation" },
];

export default function PolitiqueConfidentialitePage() {
  return (
    <>
      <PageHero
        eyebrow="Confidentialité & RGPD"
        title="Politique de"
        italicWord="confidentialité"
        subtitle="La protection de vos données personnelles est une priorité. Découvrez comment nous collectons, utilisons et protégeons vos informations conformément au RGPD."
        crumb="Politique de confidentialité"
        image={HERO_IMAGES.privacy}
      />

      <section className="section-padding bg-white">
        <div className="container-edge max-w-4xl">
          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-14 bg-[#FAF8F3] p-6 md:p-8 border-l-2 border-navy-500"
          >
            <div className="overline mb-3">Qui sommes-nous ?</div>
            <p className="text-[15px] leading-[1.8] text-[#444] mb-3">
              La société <strong className="text-navy-700">BSF</strong>, société par actions simplifiée (SAS) au capital de
              50 000 €, immatriculée au Registre du Commerce et des Sociétés de Lyon sous le numéro 793 367 780,
              dont le siège social est situé 84 rue de la Charité, 69002 Lyon, France, agit en qualité de
              <strong className="text-navy-700"> responsable du traitement des données</strong>.
            </p>
            <p className="text-[15px] leading-[1.8] text-[#444]">
              BSF attache une grande importance à la protection et au respect de votre vie privée. La présente
              politique a pour objectif de vous informer de manière transparente sur la manière dont nous
              collectons, utilisons, stockons et protégeons les informations personnelles que vous êtes amené
              à nous communiquer via le site du Campus d'Abidjan.
            </p>
            <p className="text-[15px] leading-[1.8] text-[#444] mt-3">
              Pour toute question relative à la protection de vos données personnelles, vous pouvez nous
              contacter à l'adresse suivante :{" "}
              <a href="mailto:contact@ecoledecommercedelyon.ci" className="text-red-500 hover:underline font-semibold">
                contact@ecoledecommercedelyon.ci
              </a>
            </p>
          </motion.div>

          <Section number={1} title="Responsable du traitement des données">
            <p>Le responsable du traitement des données est :</p>
            <div className="mt-4 space-y-1.5 bg-[#FAF8F3] p-5 md:p-6 border-l-2 border-navy-500">
              <div className="font-display text-lg text-navy-700 mb-2">ECOLE DE COMMERCE — BSF</div>
              <div className="text-sm text-[#555]">Société par actions simplifiée (SAS)</div>
              <div className="text-sm text-[#555]">Code APE : 8542Z</div>
              <div className="text-sm text-[#555]">SIREN : 793 367 780</div>
              <div className="text-sm text-[#555]">TVA intracommunautaire : FR93793367780</div>
              <div className="text-sm text-[#555]">Siège social : 84 rue de la Charité, 69002 Lyon, France</div>
              <div className="text-sm mt-2">
                Email :{" "}
                <a href="mailto:contact@ecoledecommercedelyon.ci" className="text-red-500 hover:underline">
                  contact@ecoledecommercedelyon.ci
                </a>
              </div>
              <div className="text-sm">Téléphone (Lyon) : +33 4 78 03 40 36</div>
            </div>
          </Section>

          <Section number={2} title="Données collectées">
            <p>Lors de l'utilisation du site, plusieurs types de données peuvent être collectés.</p>

            <h3 className="font-display text-lg text-navy-700 mt-6 mb-3">Données fournies volontairement</h3>
            <p>Lorsque vous utilisez un formulaire présent sur le site, nous pouvons collecter :</p>
            <ul className="space-y-2 mt-3">
              <Bullet>Nom et prénom</Bullet>
              <Bullet>Adresse e-mail</Bullet>
              <Bullet>Numéro de téléphone</Bullet>
              <Bullet>Informations liées à votre projet d'études</Bullet>
              <Bullet>Message ou informations complémentaires</Bullet>
            </ul>
            <p className="mt-4">Ces données sont collectées notamment via :</p>
            <ul className="space-y-2 mt-3">
              <Bullet>Formulaire de contact</Bullet>
              <Bullet>Formulaire de candidature étudiante</Bullet>
              <Bullet>Demande de brochure</Bullet>
              <Bullet>Inscription à un événement (Journées Portes Ouvertes, etc.)</Bullet>
              <Bullet>Formulaire partenaire / entreprise</Bullet>
              <Bullet>Quiz d'orientation</Bullet>
              <Bullet>Échanges avec notre assistante virtuelle Kim</Bullet>
            </ul>

            <h3 className="font-display text-lg text-navy-700 mt-6 mb-3">Données collectées automatiquement</h3>
            <p>Lorsque vous naviguez sur le site, certaines informations techniques peuvent être collectées :</p>
            <ul className="space-y-2 mt-3">
              <Bullet>Adresse IP</Bullet>
              <Bullet>Type de navigateur</Bullet>
              <Bullet>Système d'exploitation</Bullet>
              <Bullet>Pages consultées</Bullet>
              <Bullet>Durée de navigation</Bullet>
              <Bullet>Interactions avec le site</Bullet>
            </ul>
          </Section>

          <Section number={3} title="Finalité de la collecte des données">
            <p>Les données personnelles sont collectées afin de :</p>
            <ul className="space-y-2 mt-3">
              <Bullet>Répondre aux demandes d'informations</Bullet>
              <Bullet>Traiter les candidatures étudiantes</Bullet>
              <Bullet>Transmettre les documents demandés (brochures, livret étudiant, règlement intérieur)</Bullet>
              <Bullet>Informer les utilisateurs sur les formations proposées</Bullet>
              <Bullet>Analyser l'audience du site</Bullet>
              <Bullet>Améliorer l'expérience utilisateur</Bullet>
              <Bullet>Mesurer l'efficacité des campagnes publicitaires</Bullet>
            </ul>
          </Section>

          <Section number={4} title="Cookies et technologies de suivi">
            <div className="flex items-start gap-4 mb-4">
              <div className="shrink-0 w-10 h-10 bg-navy-50 flex items-center justify-center">
                <Cookie className="h-4 w-4 text-navy-500" strokeWidth={1.6} />
              </div>
              <p className="flex-1">
                Le site utilise des cookies afin d'améliorer l'expérience utilisateur et d'analyser la
                fréquentation.
              </p>
            </div>
            <p>Ces cookies peuvent être utilisés pour :</p>
            <ul className="space-y-2 mt-3">
              <Bullet>Mesurer l'audience du site</Bullet>
              <Bullet>Analyser la navigation des utilisateurs</Bullet>
              <Bullet>Améliorer les campagnes marketing</Bullet>
              <Bullet>Proposer des publicités personnalisées</Bullet>
            </ul>
            <p className="mt-4">
              Vous pouvez à tout moment refuser ou supprimer les cookies via les paramètres de votre navigateur,
              ou modifier vos préférences via notre bannière de consentement.
            </p>
          </Section>

          <Section number={5} title="Transmission des données">
            <p>
              Les données collectées peuvent être transmises uniquement aux prestataires nécessaires au
              fonctionnement du site :
            </p>
            <ul className="space-y-2 mt-3">
              <Bullet><strong className="text-navy-700">Zoho One</strong> — gestion CRM et traitement des demandes</Bullet>
              <Bullet><strong className="text-navy-700">Google</strong> — Google Analytics et Google Ads</Bullet>
              <Bullet><strong className="text-navy-700">Meta Platforms</strong> — Facebook et Instagram Ads</Bullet>
            </ul>
            <p className="mt-4">
              Ces prestataires peuvent traiter certaines données uniquement dans le cadre de leurs services.
            </p>
            <p className="mt-2 font-semibold text-navy-700">
              Aucune donnée personnelle n'est vendue à des tiers.
            </p>
          </Section>

          <Section number={6} title="Durée de conservation des données">
            <p>Les données personnelles sont conservées pendant une durée limitée :</p>
            <ul className="space-y-2 mt-3">
              <Bullet>Données prospects et candidats : <strong className="text-navy-700">5 à 10 ans maximum</strong></Bullet>
              <Bullet>Données statistiques : selon les durées de conservation prévues par Google Analytics, Google Ads et Meta</Bullet>
              <Bullet>Données techniques et logs serveur : durée limitée conformément aux obligations légales</Bullet>
            </ul>
            <p className="mt-4">Une fois ces délais expirés, les données sont supprimées ou anonymisées.</p>
          </Section>

          <Section number={7} title="Sécurité des données">
            <p>
              Le site est hébergé chez <strong className="text-navy-700">OVH</strong>, prestataire reconnu pour ses
              standards de sécurité.
            </p>
            <p className="mt-2">Plusieurs mesures sont mises en place pour protéger les données :</p>
            <ul className="space-y-2 mt-3">
              <Bullet>Connexion sécurisée via protocole HTTPS (SSL)</Bullet>
              <Bullet>Accès restreint aux données</Bullet>
              <Bullet>Sécurisation des accès administrateurs</Bullet>
              <Bullet>Outils de protection contre les intrusions</Bullet>
            </ul>
          </Section>

          <Section number={8} title="Droits des utilisateurs">
            <p>
              Conformément à la réglementation sur la protection des données, vous disposez des droits suivants :
            </p>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-navy-100 border border-navy-100 mt-5">
              {RIGHT_ICONS.map((r) => {
                const Icon = r.icon;
                return (
                  <div key={r.label} className="bg-white p-4 md:p-5 flex flex-col items-center text-center">
                    <Icon className="h-6 w-6 text-navy-500 mb-2.5" strokeWidth={1.4} />
                    <div className="text-[11px] uppercase tracking-[0.15em] text-navy-700 font-semibold leading-tight">
                      {r.label}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-6">Pour exercer ces droits, vous pouvez envoyer une demande à :</p>
            <p>
              <a href="mailto:contact@ecoledecommercedelyon.ci" className="text-red-500 hover:underline font-semibold">
                contact@ecoledecommercedelyon.ci
              </a>
            </p>
          </Section>

          <Section number={9} title="Procédure en cas de violation de données">
            <p>
              En cas de violation ou de fuite de données personnelles, les personnes concernées seront informées
              dans les meilleurs délais.
            </p>
            <p className="mt-2">Nous mettrons immédiatement en œuvre les mesures nécessaires pour :</p>
            <ul className="space-y-2 mt-3">
              <Bullet>Identifier l'origine de l'incident</Bullet>
              <Bullet>Limiter l'impact</Bullet>
              <Bullet>Sécuriser les systèmes concernés</Bullet>
            </ul>
          </Section>

          {/* CTA contact */}
          <div className="mt-16 bg-[#FAF8F3] border border-navy-100 p-8 md:p-10 text-center">
            <Shield className="h-9 w-9 text-red-500 mx-auto mb-4" strokeWidth={1.4} />
            <h3 className="font-display text-2xl md:text-3xl text-navy-700 mb-3">
              Une question sur vos <em className="not-italic red-shine">données</em> ?
            </h3>
            <p className="text-[#555] text-[14px] md:text-[15px] max-w-xl mx-auto mb-6 leading-relaxed">
              Notre Délégué à la Protection des Données est à votre écoute pour toute question relative à
              vos informations personnelles.
            </p>
            <Link to="/contact" className="btn-red" data-testid="privacy-cta-contact">
              Nous contacter
            </Link>
          </div>

          <p className="text-xs text-navy-500 mt-12 pt-6 border-t border-navy-100">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}
          </p>
        </div>
      </section>
    </>
  );
}
